// BondInfoModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';

const BondInfoModal = ({
  open,
  onClose,
  onSave,
  bondInfo,
  agentId,
  transactionId,
  alreadyUsedPowerIds = []
}) => {
  const [formValues, setFormValues] = useState({
    powerId: null,
    powerNumber: '',
    bondAmount: '',
    caseNumber: '',
    executionDate: '',
    courtDate: '',
    charges: '',
    chargeType: ''
  });
  const [availablePowers, setAvailablePowers] = useState([]);
  const [isLoadingPowers, setIsLoadingPowers] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch available powers when modal opens
  useEffect(() => {
    if (open && agentId) {
      fetchAvailablePowers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agentId]);

  // Function to fetch available powers
  const fetchAvailablePowers = async () => {
    setIsLoadingPowers(true);
    try {
      const token = localStorage.getItem('jwtToken');
      const baseURL = process.env.REACT_APP_BASE_URL;

      const response = await fetch(
        `${baseURL}PostingTransferBond/${agentId}/unexecutedPowers`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch available powers.');
      }
      const data = await response.json();

      // If editing and the selected power is not in data, add it
      if (bondInfo && bondInfo.powerId) {
        const powerExists = data.some((power) => power.powerId === bondInfo.powerId);
        if (!powerExists) {
          data.push({
            powerId: bondInfo.powerId,
            powerNumber: bondInfo.powerNumber,
          });
        }
      }

      const filtered = data.filter(
        (p) => !alreadyUsedPowerIds.includes(p.powerId)
      );

      setAvailablePowers(filtered); // Assuming data is an array of { powerId, powerNumber }

      // Now set the formValues
      if (bondInfo) {
        setFormValues({
          powerId: bondInfo.powerId || null,
          powerNumber: bondInfo.powerNumber || '',
          bondAmount: bondInfo.bondAmount || '',
          caseNumber: bondInfo.caseNumber || '',
          executionDate: bondInfo.executionDate ? bondInfo.executionDate.split('T')[0] : '',
          courtDate: bondInfo.courtDate ? bondInfo.courtDate.split('T')[0] : '',
          charges: bondInfo.charges || '',
          chargeType: bondInfo.chargeType || ''
        });
      } else {
        // Reset form for adding new bond info
        setFormValues({
          powerId: null,
          powerNumber: '',
          bondAmount: '',
          caseNumber: '',
          executionDate: '',
          courtDate: '',
          charges: '',
          chargeType: ''
        });
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Error fetching available powers.');
    } finally {
      setIsLoadingPowers(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePowerChange = (event, newValue) => {
    if (newValue) {
      setFormValues({
        ...formValues,
        powerId: newValue.powerId,
        powerNumber: newValue.powerNumber,
      });
    } else {
      setFormValues({
        ...formValues,
        powerId: null,
        powerNumber: '',
      });
    }
  };

  const handleSave = async () => {
    // Validate required fields
    if (!formValues.powerId || !formValues.bondAmount || !formValues.executionDate) {
      toast.error('Power Number, Bond Amount, and Execution Date are required.');
      return;
    }

      // Check if executionDate is in the future
    const today = new Date().toISOString().split('T')[0]; // Get today's date in 'YYYY-MM-DD' format
    if (formValues.executionDate > today) {
      toast.error('Execution Date cannot be in the future.');
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem('jwtToken');
      const baseURL = process.env.REACT_APP_BASE_URL;

      const transferPowerData = {
        TransferBondId: bondInfo ? bondInfo.transferBondId : null,
        PowerNumber: formValues.powerNumber,
        BondAmount: parseFloat(formValues.bondAmount),
        CaseNumber: formValues.caseNumber || null,
        ExecutionDate: formValues.executionDate ? new Date(formValues.executionDate) : null,
        CourtDate: formValues.courtDate ? new Date(formValues.courtDate) : null,
        Charges: formValues.charges || null,
        ChargeType: formValues.chargeType || null,
      };

      let response;
      if (bondInfo) {
        // Update existing bond info
        response = await fetch(
          `${baseURL}PostingTransferBond/${transactionId}/transferPowers/${bondInfo.transferBondId}`,
          {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(transferPowerData),
          }
        );
      } else {
        // Add new bond info
        response = await fetch(
          `${baseURL}PostingTransferBond/${transactionId}/transferPowers`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(transferPowerData),
          }
        );
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save bond information.');
      }

      toast.success(
        bondInfo ? 'Bond information updated successfully.' : 'Bond information added successfully.'
      );

      onSave(); // Notify parent to refetch data
      onClose(); // Close modal
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'An error occurred while saving.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{bondInfo ? 'Edit Bond Information' : 'Add Bond Information'}</DialogTitle>
      <DialogContent>
        {isLoadingPowers ? (
          <Grid container justifyContent="center" alignItems="center" style={{ height: '200px' }}>
            <CircularProgress />
          </Grid>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={availablePowers}
                getOptionLabel={(option) => option.powerNumber || ''}
                value={
                  availablePowers.find((power) => power.powerId === formValues.powerId) ||
                  (formValues.powerId && {
                    powerId: formValues.powerId,
                    powerNumber: formValues.powerNumber,
                  }) ||
                  null
                }
                onChange={handlePowerChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Power Number"
                    required
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        '& .MuiFormLabel-asterisk': {
                          color: 'red',
                          fontSize: '1.2em',
                        },
                      },
                    }}
                  />
                )}
                disableClearable
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Bond Amount"
                name="bondAmount"
                type="number"
                value={formValues.bondAmount}
                onChange={handleChange}
                fullWidth
                required
                inputProps={{ min: '0', step: '0.01' }}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    '& .MuiFormLabel-asterisk': {
                      color: 'red',
                      fontSize: '1.2em',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Case Number"
                name="caseNumber"
                value={formValues.caseNumber}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Execution Date"
                name="executionDate"
                type="date"
                value={formValues.executionDate}
                onChange={handleChange}
                fullWidth
                required
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    '& .MuiFormLabel-asterisk': {
                      color: 'red',
                      fontSize: '1.2em',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Court Date"
                name="courtDate"
                type="date"
                value={formValues.courtDate}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Charge Type"
                name="chargeType"
                value={formValues.chargeType}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Charges"
                name="charges"
                value={formValues.charges}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                inputProps={{ maxLength: 250 }}
                helperText={`${formValues.charges.length}/250`}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="secondary"
          variant="outlined"
          size="large"
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          size="large"
          disabled={isSaving || isLoadingPowers}
        >
          {isSaving ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BondInfoModal;
